import React, { useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader, Panel, Banner } from "../ds";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { BrandCard } from "../components/social/BrandCard";
import { QueueTable, SOCIAL_STATUSES as STATUSES } from "../components/social/QueueTable";
import { Gallery } from "../components/social/Gallery";
import { MonthSelect, currentDubaiMonth } from "../components/social/MonthSelect";
import { DsButton } from "../ds";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";


export default function Social() {
  const qc = useQueryClient();
  const [brand, setBrand] = useState("all");
  const [status, setStatus] = useState("all");
  const [month, setMonth] = useState(currentDubaiMonth);

  const { data: brandsData } = useQuery({
    queryKey: ["social-brands"],
    queryFn: async () => (await api.get("/social/brands")).data,
    refetchInterval: 30000,
  });
  const { data: itemsData } = useQuery({
    queryKey: ["social-items", brand, status, month],
    queryFn: async () => (await api.get("/social/items", {
      params: { brand: brand === "all" ? undefined : brand, status: status === "all" ? undefined : status, month },
    })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
  });

  const brands = brandsData?.brands || [];
  const items = itemsData?.items || [];
  const brandNames = Object.fromEntries(brands.map((b) => [b.slug, b.name]));
  const failedOpen = brands.reduce((n, b) => n + (b.failed_open || 0), 0);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["social-brands"] });
    qc.invalidateQueries({ queryKey: ["social-items"] });
    qc.invalidateQueries({ queryKey: ["social-months"] });
  };
  const dismissAll = async () => {
    try {
      const { data } = await api.post("/social/items/dismiss-failed");
      toast.success(`Dismissed ${data.dismissed} failed post${data.dismissed === 1 ? "" : "s"}.`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="social-page">
      <PageHeader title="Social Studio" subtitle="Publish queue handed to n8n. Only brands with Publishing ON are readable by n8n." />
      {failedOpen > 0 && (
        <Banner
          label="FAILED"
          testId="social-failed-banner"
          action={<DsButton variant="no" className="h-8" onClick={dismissAll} data-testid="social-dismiss-all-btn">Dismiss all</DsButton>}
        >
          <span data-testid="social-failed-count">{failedOpen}</span> post{failedOpen === 1 ? "" : "s"} failed to publish. Review the highlighted rows below.
        </Banner>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {brands.map((b) => <BrandCard key={b.slug} brand={b} onChanged={refresh} />)}
        {brands.length === 0 && <p className="text-sm text-[color:var(--muted)]" data-testid="social-no-brands">No brands yet.</p>}
      </div>
      {brands.length > 0 && <Gallery brands={brands} onChanged={refresh} />}
      <Panel
        label={`Publish queue · ${items.length}`}
        testId="social-queue-panel"
        action={(
          <div className="flex flex-wrap gap-2 justify-end">
            <MonthSelect value={month} onChange={setMonth} brand={brand} status={status} testId="social-filter-month" />
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger className="h-8 w-[180px]" data-testid="social-filter-brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-[200px]" data-testid="social-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      >
        <QueueTable items={items} brandNames={brandNames} onChanged={refresh} />
      </Panel>
    </div>
  );
}
